import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useUserLocation, type UserLocation } from "./useUserLocation";
import {
  findRoute,
  getCurrentInstructionIndex,
  isRouteDebugEnabled,
  labelRoutePath,
  type Route,
  type RoutingOptions,
  type PathNetwork,
  getDistanceMeters,
  type LandmarkPOI,
} from "@wondermaps/maps";
import { toast } from "@wondermaps/ui";
import { pointToLineDistance, point, lineString, nearestPointOnLine } from "@turf/turf";

/** Arrival threshold in meters */
const ARRIVAL_DISTANCE = 15;

/** Distance threshold for off-path detection in meters */
const OFF_PATH_DISTANCE = 100;

/** Hysteresis: must be within this distance to clear off-path */
const ON_PATH_DISTANCE = 80;

/** Walking speed in m/s (matches wayfinding.ts) */
const WALKING_SPEED = 1.4;

export interface POIDestination {
  id: string;
  name: string;
  coords: [number, number]; // [lng, lat]
  categoryName?: string | null;
  categoryIcon?: string | null;
}

export interface UseWayfindingReturn {
  route: Route | null;
  isNavigating: boolean;
  isPreviewing: boolean;
  destination: POIDestination | null;
  previewRoute: (poi: POIDestination) => boolean;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  /** Return from active navigation back to preview state (Cancel button) */
  backToPreview: () => void;
  checkArrival: (userLocation: UserLocation) => boolean;
  completedSegmentIndex: number;
  currentInstructionIndex: number;
  isFallbackRoute: boolean;
  isOffPath: boolean;
  reroute: () => void;
  remainingDistance: number;
  remainingTime: number;
}

/**
 * Calculates the index of the segment closest to the user's position.
 * All segments before this index are considered "completed".
 * @param userPosition - [lng, lat] of user
 * @param routePath - Array of [lng, lat] coordinates forming the route
 * @returns The segment index (0-based) where the user currently is. Returns -1 if no valid route.
 */
export function getCompletedSegmentIndex(
  userPosition: [number, number],
  routePath: [number, number][]
): number {
  if (!routePath || routePath.length < 2) {
    return -1;
  }

  const userPoint = point(userPosition);
  const line = lineString(routePath);
  const snapped = nearestPointOnLine(line, userPoint);

  // segmentIndex tells us which segment (0-based) the closest point falls on
  // All segments with index < segmentIndex are completed
  return snapped.properties.index ?? -1;
}

/**
 * Hook to manage wayfinding navigation state
 * @param network - The path network for routing (from usePathNetwork)
 * @param landmarkPOIs - Optional array of POIs for landmark-based turn instructions
 * @param accessibleMode - Whether to apply accessible-routing constraints
 * @param nodeKindRefs - Optional reference sets used to label pathHead/pathTail
 *   nodes as junction/polygon-vertex/midline in the [wm:route] diagnostic.
 *   Pass through from `usePathNetwork`. Only consumed when localStorage.wmRouteDebug=1.
 */
export function useWayfinding(
  network: PathNetwork | null,
  landmarkPOIs?: LandmarkPOI[],
  accessibleMode?: boolean,
  nodeKindRefs?: { lineEndpointKeys: Set<string>; polygonVertexKeys: Set<string> } | null,
): UseWayfindingReturn {
  const { t } = useTranslation("map");
  const { location } = useUserLocation();

  const [route, setRoute] = useState<Route | null>(null);
  const [destination, setDestination] = useState<POIDestination | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isFallbackRoute, setIsFallbackRoute] = useState(false);
  const [isOffPath, setIsOffPath] = useState(false);
  const offPathToastShown = useRef(false);

  /** Shared route calculation logic */
  const calcRoute = useCallback(
    (poi: POIDestination): { route: Route; fallback: boolean } | null => {
      if (!network || !location) return null;

      const userCoords: [number, number] = [
        location.coords.longitude,
        location.coords.latitude,
      ];

      const routingOptions: RoutingOptions | undefined = accessibleMode
        ? { avoidStairs: true, avoidSteepGrade: true }
        : undefined;

      let calculatedRoute = findRoute(network, userCoords, poi.coords, landmarkPOIs, routingOptions);
      let fallback = false;

      if (!calculatedRoute && accessibleMode) {
        calculatedRoute = findRoute(network, userCoords, poi.coords, landmarkPOIs);
        fallback = true;
      }

      // Diagnostic: visitor-side route output. Gated behind
      // localStorage.wmRouteDebug=1. Dumps the full path with per-step edge
      // distances so an off-path "jump" (consecutive coords with no graph edge
      // between them) is immediately visible — that's the bug fingerprint we
      // need for the recurring admin/visitor route divergence question.
      if (isRouteDebugEnabled() && network && nodeKindRefs && calculatedRoute) {
        // eslint-disable-next-line no-console
        console.log("[wm:route] visitor/calc", {
          start: userCoords,
          end: poi.coords,
          poiName: poi.name,
          totalDistance: calculatedRoute.totalDistance,
          pathLength: calculatedRoute.path.length,
          path: labelRoutePath(calculatedRoute.path, network, nodeKindRefs, calculatedRoute.overlay),
          fallback,
        });
      }

      return calculatedRoute ? { route: calculatedRoute, fallback } : null;
    },
    [network, location, landmarkPOIs, accessibleMode]
  );

  // Off-path detection with hysteresis
  useEffect(() => {
    if (!isNavigating || !route || !location) {
      return;
    }

    const userPoint = point([location.coords.longitude, location.coords.latitude]);

    let minDistance = Infinity;
    for (let i = 0; i < route.path.length - 1; i++) {
      const segment = lineString([route.path[i], route.path[i + 1]]);
      const dist = pointToLineDistance(userPoint, segment, { units: "meters" });
      if (dist < minDistance) {
        minDistance = dist;
      }
    }

    if (minDistance > OFF_PATH_DISTANCE) {
      setIsOffPath(true);
      if (!offPathToastShown.current) {
        offPathToastShown.current = true;
        toast({
          title: t("directions.offRoute"),
          duration: 5000,
        });
      }
    } else if (minDistance < ON_PATH_DISTANCE) {
      setIsOffPath(false);
    }
  }, [isNavigating, route, location, t]);

  /** Preview route without starting navigation */
  const previewRoute = useCallback(
    (poi: POIDestination): boolean => {
      const result = calcRoute(poi);
      if (result) {
        offPathToastShown.current = false;
        setIsOffPath(false);
        setRoute(result.route);
        setDestination(poi);
        setIsPreviewing(true);
        setIsNavigating(false);
        setIsFallbackRoute(result.fallback);
        return true;
      }
      return false;
    },
    [calcRoute]
  );

  /** Confirm previewed route → start navigation */
  const confirmNavigation = useCallback(() => {
    if (route && destination) {
      setIsPreviewing(false);
      setIsNavigating(true);
    }
  }, [route, destination]);

  const backToPreview = useCallback(() => {
    if (route && destination) {
      setIsNavigating(false);
      setIsPreviewing(true);
    }
  }, [route, destination]);

  const cancelNavigation = useCallback(() => {
    offPathToastShown.current = false;
    setRoute(null);
    setDestination(null);
    setIsNavigating(false);
    setIsPreviewing(false);
    setIsFallbackRoute(false);
    setIsOffPath(false);
  }, []);

  /** Re-route from current position to same destination */
  const reroute = useCallback(() => {
    if (!destination) return;
    const result = calcRoute(destination);
    if (result) {
      offPathToastShown.current = false;
      setIsOffPath(false);
      setRoute(result.route);
      setIsFallbackRoute(result.fallback);
    }
  }, [destination, calcRoute]);

  const checkArrival = useCallback(
    (userLocation: UserLocation): boolean => {
      if (!destination || !isNavigating) {
        return false;
      }

      const distance = getDistanceMeters(
        { x: userLocation.coords.longitude, y: userLocation.coords.latitude },
        { x: destination.coords[0], y: destination.coords[1] }
      );

      return distance <= ARRIVAL_DISTANCE;
    },
    [destination, isNavigating]
  );

  // Re-route when accessibleMode changes during active navigation.
  // Deferred via setTimeout so React renders the toggle state change immediately
  // before the synchronous findRoute call blocks the main thread.
  // Cleanup cancels any pending recalculation on rapid toggling.
  useEffect(() => {
    if ((!isNavigating && !isPreviewing) || !destination || !network || !location) return;

    const timer = setTimeout(() => {
      const userCoords: [number, number] = [
        location.coords.longitude,
        location.coords.latitude,
      ];

      const routingOptions: RoutingOptions | undefined = accessibleMode
        ? { avoidStairs: true, avoidSteepGrade: true }
        : undefined;

      let newRoute = findRoute(network, userCoords, destination.coords, landmarkPOIs, routingOptions);
      let fallback = false;

      if (!newRoute && accessibleMode) {
        newRoute = findRoute(network, userCoords, destination.coords, landmarkPOIs);
        fallback = true;
      }

      if (newRoute) {
        setRoute(newRoute);
        setIsFallbackRoute(fallback);
      }
    }, 0);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-route on accessibleMode change
  }, [accessibleMode]);

  // Calculate completed segment index based on user position
  const completedSegmentIndex = useMemo(() => {
    if (!isNavigating || !route || !location) {
      return -1;
    }
    const userCoords: [number, number] = [
      location.coords.longitude,
      location.coords.latitude,
    ];
    return getCompletedSegmentIndex(userCoords, route.path);
  }, [isNavigating, route, location]);

  // Derive current instruction from segment progress
  const currentInstructionIndex = useMemo(() => {
    if (!route || completedSegmentIndex < 0) return 0;
    return getCurrentInstructionIndex(route.instructions, completedSegmentIndex);
  }, [route, completedSegmentIndex]);

  // Compute remaining distance + time from current segment to end
  const { remainingDistance, remainingTime } = useMemo(() => {
    if (!route || completedSegmentIndex < 0) {
      return { remainingDistance: route?.totalDistance ?? 0, remainingTime: route?.estimatedTime ?? 0 };
    }
    let dist = 0;
    const path = route.path;
    for (let i = completedSegmentIndex; i < path.length - 1; i++) {
      const [x1, y1] = path[i];
      const [x2, y2] = path[i + 1];
      // Quick haversine approximation — calcDistance is imported via the maps package
      // but it's not exposed here, so use turf's approach
      const dx = (x2 - x1) * Math.cos(((y1 + y2) / 2) * Math.PI / 180) * 111320;
      const dy = (y2 - y1) * 111320;
      dist += Math.sqrt(dx * dx + dy * dy);
    }
    return { remainingDistance: dist, remainingTime: dist / WALKING_SPEED };
  }, [route, completedSegmentIndex]);

  return {
    route,
    isNavigating,
    isPreviewing,
    destination,
    previewRoute,
    confirmNavigation,
    cancelNavigation,
    backToPreview,
    checkArrival,
    completedSegmentIndex,
    currentInstructionIndex,
    isFallbackRoute,
    isOffPath,
    reroute,
    remainingDistance,
    remainingTime,
  };
}
